// Server functions for Model operations
import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { db } from '@/src/server/db'
import { auth } from '@/auth'
import { getSessionFromRequest } from '@/src/server/lib/accessControl'
import { PERMISSIONS } from '@/permission.config'
import { hasPermission } from '@/src/server/lib/permissions'
import { z } from 'zod'

// Zod schemas for input validation
const GetModelByIdInput = z.object({
  id: z.string(),
})

const UpsertModelInput = z.object({
  id: z.string().optional(),
  name: z.string(),
  provider: z.string(),
  model: z.string(),
  label: z.string(),
  description: z.string().optional(),
  enabled: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  function: z.string(),
  permissions: z.array(z.string()),
  order: z.number().optional(),
  thinkingAmount: z.string().optional(),
  temperature: z.number().optional(),
})

const DeleteModelInput = z.object({
  id: z.string(),
})

const ReorderModelsInput = z.object({
  ids: z.array(z.string()),
})

// List models - if all=true, returns all models (including disabled), otherwise only enabled
export const listModels = createServerFn({ method: 'GET' })
  .inputValidator((data?: { all?: boolean }) => data || {})
  .handler(async ({ data }) => {
    const models = await db.aiModel.findMany({
      where: data?.all ? {} : { enabled: true },
      orderBy: { order: 'asc' },
    })
    return models
  })

export const getModelById = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetModelByIdInput.parse(data))
  .handler(async ({ data }) => {
    const model = await db.aiModel.findUnique({ where: { id: data.id } })
    return model
  })

export const upsertModel = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => UpsertModelInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    
    // SECURITY: Only admins can modify models
    const isAdmin = await hasPermission(session, [PERMISSIONS.ADMIN])
    if (!isAdmin) {
      throw new Error('Unauthorized: Admin access required')
    }
    
    const { id, ...rest } = data
    // Cast thinkingAmount to enum type if provided
    const updateData = {
      ...rest,
      ...(rest.thinkingAmount && { thinkingAmount: rest.thinkingAmount as any }),
    }
    if (id) {
      const result = await db.aiModel.update({ where: { id }, data: updateData })
      // Cache invalidation disabled - Prisma Accelerate not configured
      // await db.$accelerate.invalidate({ tags: ['modelList', 'modelListAll'] })
      return result
    } else {
      const result = await db.aiModel.create({ data: updateData as any })
      // Cache invalidation disabled - Prisma Accelerate not configured
      // await db.$accelerate.invalidate({ tags: ['modelList', 'modelListAll'] })
      return result
    }
  })

export const deleteModel = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => DeleteModelInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    
    // SECURITY: Only admins can delete models
    const isAdmin = await hasPermission(session, [PERMISSIONS.ADMIN])
    if (!isAdmin) {
      throw new Error('Unauthorized: Admin access required')
    }
    
    const result = await db.aiModel.delete({ where: { id: data.id } })
    // Cache invalidation disabled - Prisma Accelerate not configured
    // await db.$accelerate.invalidate({ tags: ['modelList', 'modelListAll'] })
    return result
  })

export const reorderModels = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => ReorderModelsInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    
    // SECURITY: Only admins can reorder models
    const isAdmin = await hasPermission(session, [PERMISSIONS.ADMIN])
    if (!isAdmin) {
      throw new Error('Unauthorized: Admin access required')
    }
    
    for (let i = 0; i < data.ids.length; i++) {
      await db.aiModel.update({ where: { id: data.ids[i] }, data: { order: i } })
    }
    // Cache invalidation disabled - Prisma Accelerate not configured
    // await db.$accelerate.invalidate({ tags: ['modelList', 'modelListAll'] })
    return { success: true }
  })
